import { useApplicationModals } from "app/common/hooks";
import { ModalMolecule } from "app/components/molecules/modal";
import QRCodePdf from "app/components/organisms/pdf/QRCode/QRCodePdf";
import QRGenerator from "app/components/atoms/qrcode/QRGenerator";
import { useState, useEffect, useMemo } from "react";

/** Batas maksimum lembar cetak QR code yang diizinkan untuk mencegah UI freeze / OOM */
const MAX_PRINT_COUNT = 999;
const MIN_PRINT_COUNT = 1;

/**
 * Jumlah kartu yang benar-benar digambar sebagai pratinjau.
 *
 * Untuk barcode varian SELURUH salinan isinya identik — hanya `id` sintetisnya
 * yang berbeda — jadi menggambar 999 kartu tidak memperlihatkan apa pun yang
 * tidak sudah terlihat pada kartu pertama, sementara tiap kartu memanggil
 * `QRCode.toDataURL` sendiri. PDF tetap menerima jumlah penuh.
 */
const PREVIEW_LIMIT = 20;

/**
 * clampPrintCount
 *
 * Memvalidasi dan membatasi nilai jumlah lembar cetak agar selalu berada
 * dalam rentang aman [MIN_PRINT_COUNT, MAX_PRINT_COUNT].
 *
 * @param {number|string} value - Nilai input yang akan divalidasi.
 * @returns {number} Nilai integer yang sudah dibatasi dalam rentang aman.
 * @sideEffects Tidak ada efek samping (pure function).
 */
const clampPrintCount = (value) => {
  const parsed = parseInt(value, 10);
  if (isNaN(parsed) || parsed < MIN_PRINT_COUNT) {
    return MIN_PRINT_COUNT;
  }
  if (parsed > MAX_PRINT_COUNT) {
    return MAX_PRINT_COUNT;
  }
  return parsed;
};

/**
 * ViewQrCodeModal
 * 
 * Modul modal untuk menampilkan preview dan fasilitas cetak PDF QR Code.
 * Mendukung pencetakan barcode unik (per-pcs) maupun barcode varian (non-unik)
 * dengan batas aman kuantitas cetak (maksimum 999 lembar).
 *
 * @param {Object} props - Component properties.
 * @param {Function} [props.onSuccess=() => {}] - Callback yang dipanggil saat modal ditutup dengan sukses.
 * @returns {JSX.Element} Element modal cetak QR code.
 * @sideEffects
 * - Membaca dan memanipulasi modal state dari useApplicationModals hook.
 * - Memperbarui state printCount dan selectedFormat.
 */
function ViewQrCodeModal({ onSuccess = () => { } }) {
  const { showStates, getPayloads, closeModal } = useApplicationModals();
  const payload = getPayloads("qrCode.view");
  const product = payload?.product;
  const multiple = payload?.multiple;
  const isVariant = payload?.isVariant || false;
  const [selectedFormat, setSelectedFormat] = useState("3/2");
  // Nilai TERKOMIT: hanya berubah saat input selesai diedit (blur), bukan tiap
  // ketikan. `QRCodePdf` merender kedua dokumennya secara eager, dan tiap label
  // di dalamnya memanggil `QRCode.toDataURL` sendiri — kalau nilai ini ikut
  // berubah per ketikan, mengetik "999" menyeret ratusan label melewati tiga
  // kali render dokumen berturut-turut.
  const [printCount, setPrintCount] = useState(() => clampPrintCount(payload?.count || 1));
  // Teks mentah di kotak input, supaya field bisa dikosongkan dulu sebelum
  // diketik ulang — meng-clamp di `onChange` membuat "" langsung memantul ke 1.
  const [printCountInput, setPrintCountInput] = useState(() =>
    String(clampPrintCount(payload?.count || 1))
  );

  useEffect(() => {
    if (payload?.count !== undefined && payload?.count !== null) {
      const next = clampPrintCount(payload.count);
      setPrintCount(next);
      setPrintCountInput(String(next));
    }
  }, [payload?.count]);

  /**
   * handleClose
   * Menutup modal qrCode.view.
   *
   * @returns {void}
   * @sideEffects Memanggil closeModal("qrCode.view").
   */
  const handleClose = () => {
    closeModal("qrCode.view");
  };

  /**
   * handleSuccess
   * Menutup modal qrCode.view dan menjalankan callback onSuccess.
   *
   * @returns {void}
   * @sideEffects
   * - Memanggil closeModal("qrCode.view").
   * - Mengeksekusi onSuccess callback.
   */
  const handleSuccess = () => {
    closeModal("qrCode.view");
    onSuccess();
  };

  // Pastikan kuantitas selalu dibatasi sebelum dialokasikan ke memori
  const safeCount = clampPrintCount(printCount);
  const baseProduct = Array.isArray(product) ? product[0] : product;

  const displayProducts = useMemo(() => {
    if (isVariant) {
      return Array.from({ length: safeCount }, (_, index) => ({
        ...baseProduct,
        id: baseProduct ? `${baseProduct.id}-${index}` : index,
      }));
    }
    return Array.isArray(product) ? product : (product ? [product] : []);
  }, [isVariant, safeCount, baseProduct, product]);

  // Barcode unik: tiap kartu isinya berbeda, semuanya wajib terlihat.
  const previewProducts = isVariant
    ? displayProducts.slice(0, PREVIEW_LIMIT)
    : displayProducts;
  const hiddenPreviewCount = displayProducts.length - previewProducts.length;

  return (
    <ModalMolecule
      show={showStates["qrCode.view"]}
      title={isVariant ? "QR Code Varian (Non-Unik)" : "QR Code"}
      onClose={handleClose}
      size="sm"
    >
      <div className="mb-4 space-y-3">
        <div className="flex gap-2 justify-center">
          <button data-testid="modal-view-qr-code-size-3x2-button"
            onClick={() => setSelectedFormat("3/2")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedFormat === "3/2"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            4x6 (Portrait)
          </button>
          <button data-testid="modal-view-qr-code-size-5x2-button"
            onClick={() => setSelectedFormat("5/2")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedFormat === "5/2"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            5x2 (Landscape)
          </button>
        </div>

        {/* Input Pengaturan Jumlah Cetak untuk Barcode Varian (Non-Unik) */}
        {isVariant && (
          <div className="flex flex-col items-center justify-center gap-1 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                Jumlah Lembar Cetak:
              </label>
              <input data-testid="modal-view-qr-code-print-count-input"
                type="number"
                min={MIN_PRINT_COUNT}
                max={MAX_PRINT_COUNT}
                value={printCountInput}
                onChange={(e) => setPrintCountInput(e.target.value)}
                onBlur={(e) => {
                  const next = clampPrintCount(e.target.value);
                  setPrintCount(next);
                  setPrintCountInput(String(next));
                }}
                className="w-20 px-2 py-1 text-center font-bold text-sm border rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            <span className="text-[10px] text-gray-500 dark:text-gray-400">
              Maksimum {MAX_PRINT_COUNT} lembar per sesi cetak
            </span>
          </div>
        )}
      </div>

      <div id="divToPrint" className="max-h-80 overflow-y-auto mb-4">
        {multiple || isVariant ? (
          <div className="grid grid-cols-2 gap-2 pr-2">
            {previewProducts?.map((item, idx) => (
              <QRGenerator key={item.id || idx} product={item} size="small" format={selectedFormat} />
            ))}
            {hiddenPreviewCount > 0 && (
              <div className="col-span-2 py-3 text-center text-xs text-gray-500 dark:text-gray-400">
                ...dan {hiddenPreviewCount} lembar lainnya (semuanya identik, ikut tercetak di PDF)
              </div>
            )}
          </div>
        ) : (
          <QRGenerator product={product} format={selectedFormat} />
        )}
      </div>

      {/* ponytail: tiap label di PDF meng-encode QR-nya sendiri, jadi 999 salinan
          varian yang identik = 999 encode per dokumen (dan ada dua dokumen).
          Sekarang itu hanya berjalan sekali per nilai yang di-commit, bukan per
          ketikan. Kalau masih terasa berat, cache `QRCode.toDataURL` per string. */}
      <QRCodePdf products={displayProducts} selectedFormat={selectedFormat} />
    </ModalMolecule>
  );
}

export default ViewQrCodeModal;

